import { Comment, CommentDataType } from '../../shared/components/Comments/Comments.types'

export type CommentsViewNavigationTarget = {
    pathname: string
    autoOpenData?: {
        isAutoOpenPanel: true
        autoOpenData: Record<string, unknown>
        previousDataStack: Array<{ path: string; data: Record<string, never> }>
    }
}

export type CommentsViewTab = {
    id: CommentsViewTabId
    label: string
    emptyStateTitle: string
    emptyStateSubtitle: string
    getTypeLabel: (dataType: CommentDataType) => string
    getLocationLabel: (comment: CommentsViewComment) => string
    getLocationTarget: (comment: CommentsViewComment, currentPath: string) => CommentsViewNavigationTarget | null
}

export type CommentsViewDomainTab = CommentsViewTab & {
    id: CommentsViewDomainTabId
}

export type CommentsViewDomainTabId = 'tpp' | 'strategicImperative' | 'valueMessage' | 'activity' | 'evidence'

export type CommentsViewTabId = 'all' | CommentsViewDomainTabId

export const COMMENTS_VIEW_DOMAIN_TAB_IDS: CommentsViewDomainTabId[] = [
    'strategicImperative',
    'valueMessage',
    'activity',
    'evidence',
    'tpp',
]

export type CommentsViewColumnId = 'user' | 'comment' | 'type' | 'location' | 'date' | 'updated' | 'resolved' | 'action'

export type CommentsViewLocation = {
    type: 'table' | 'item' | 'missing'
    domain: CommentsViewDomainTabId | null
    id: string | null
    name: string | null
}

export type CommentsViewCommentExtras = {
    category?: string | null
    categoryId?: string | null
    status?: string | null
}

export type CommentsViewComment = Comment & {
    dataType: CommentDataType
    dataId?: string
    action?: string
    location: CommentsViewLocation
    extras?: CommentsViewCommentExtras
    updatedAt: string
}

export type CommentsViewCommonFilterId = 'isResolved' | 'user' | 'type'

export type CommentsViewDomainFilterId = 'category' | 'status'

export type CommentsViewFilterOption = {
    id: string
    label: string
    order?: number
}

export type CommentsViewAppliedFilterEntry<TDefinition> = {
    definition: TDefinition
    options: CommentsViewFilterOption[]
    selectedOptionIds: string[]
}

export type CommentsViewSelectFilterConfig = {
    id: string
    label: string
    placeholder: string
    options: CommentsViewFilterOption[]
    selectedOptionIds: string[]
    isActive: boolean
    onChange: (selectedOptionIds: string[]) => void
    onClear: () => void
}

export type CommentsViewFilterConfigData = Omit<CommentsViewSelectFilterConfig, 'onChange' | 'onClear'>

export type CommentsViewAllFilterGroup = {
    id: CommentsViewDomainTabId
    label: string
    filters: Array<{
        id: CommentsViewDomainFilterId
        label: string
        placeholder: string
        options: CommentsViewFilterOption[]
        selectedOptionIds: string[]
    }>
}

export type CommentsViewCommonFilterDefinition = {
    id: CommentsViewCommonFilterId
    label: string
    placeholder: string
    getOptions: (comments: CommentsViewComment[]) => CommentsViewFilterOption[]
    matches: (comment: CommentsViewComment, selectedOptionIds: string[]) => boolean
}

export type CommentsViewDomainFilterDefinition = {
    id: CommentsViewDomainFilterId
    label: string
    placeholder: string
    multiple?: boolean
    getOptions: (comments: CommentsViewComment[]) => CommentsViewFilterOption[]
    matches: (comment: CommentsViewComment, selectedOptionIds: string[]) => boolean
}

export type CommentsViewCommonFiltersState = Partial<Record<CommentsViewCommonFilterId, string[]>>

export type CommentsViewDomainFiltersState = Partial<
    Record<CommentsViewDomainTabId, Partial<Record<CommentsViewDomainFilterId, string[]>>>
>

export type CommentsViewDateRangeFiltersState = {
    from: string | null
    to: string | null
}

export type CommentsViewFiltersState = {
    common: CommentsViewCommonFiltersState
    dateRange: CommentsViewDateRangeFiltersState
    domains: CommentsViewDomainFiltersState
}

export type UpdateCommentsViewCommentPayload = {
    commentId: string
    domainTabId?: CommentsViewDomainTabId
    action?: string
    isResolved?: boolean
}
